import type * as Monaco from "monaco-editor";
import { IPlannedCodeInsertion } from "./ai-code-insertion";

/**
 * Shows a planned edit inside a Monaco editor WITHOUT writing it: the new lines are drawn
 * in a view zone and the lines it would overwrite are struck through, so the model only
 * changes when the user confirms. Discarding leaves nothing to undo.
 */
export interface IMonacoPendingEditOptions {
  /** The user typed while the preview was up, so the planned range no longer means anything. */
  onInvalidated: () => void;
}

const LEADING_NEWLINE = /^\r?\n/;

export class MonacoPendingEdit {
  private readonly monaco: typeof Monaco;

  private readonly editor: Monaco.editor.IStandaloneCodeEditor;

  private readonly options: IMonacoPendingEditOptions;

  private plan: IPlannedCodeInsertion | null = null;

  private viewZoneId: string | null = null;

  private decorations: Monaco.editor.IEditorDecorationsCollection | null = null;

  private contentSubscription: Monaco.IDisposable | null = null;

  private isCommitting: boolean = false;

  /** Guards the async colorizer against a preview that was replaced while it was running. */
  private renderToken: number = 0;

  constructor(
    monacoInstance: typeof Monaco,
    editor: Monaco.editor.IStandaloneCodeEditor,
    options: IMonacoPendingEditOptions
  ) {
    this.monaco = monacoInstance;
    this.editor = editor;
    this.options = options;
  }

  show(plan: IPlannedCodeInsertion): void {
    const model: Monaco.editor.ITextModel | null = this.editor.getModel();
    if (!model) return;

    this.clear();
    this.plan = plan;

    const previewLines: string[] = plan.text.replace(LEADING_NEWLINE, "").split(/\r\n|\r|\n/);
    this.addViewZone(plan, previewLines, model.getLanguageId());
    this.markReplacedLines(plan);

    this.contentSubscription = model.onDidChangeContent((): void => {
      if (this.isCommitting) return;
      this.clear();
      this.options.onInvalidated();
    });

    this.editor.revealLineInCenterIfOutsideViewport(Math.max(plan.previewAfterLineNumber, 1));
  }

  /** Writes the pending edit as one undoable change and returns the model's new value. */
  commit(source: string): string | null {
    const model: Monaco.editor.ITextModel | null = this.editor.getModel();
    const plan: IPlannedCodeInsertion | null = this.plan;
    if (!model || !plan) return null;

    this.isCommitting = true;
    try {
      this.editor.pushUndoStop();
      this.editor.executeEdits(source, [
        {
          range: new this.monaco.Range(
            plan.range.startLineNumber,
            plan.range.startColumn,
            plan.range.endLineNumber,
            plan.range.endColumn
          ),
          text: plan.text,
          forceMoveMarkers: true
        }
      ]);
      this.editor.pushUndoStop();
    } finally {
      this.isCommitting = false;
    }

    this.clear();
    this.editor.focus();
    return model.getValue();
  }

  /** Takes the preview off the screen. The model was never touched, so nothing is undone. */
  clear(): void {
    this.renderToken += 1;
    this.plan = null;

    if (this.viewZoneId) {
      const zoneId: string = this.viewZoneId;
      this.editor.changeViewZones((accessor: Monaco.editor.IViewZoneChangeAccessor): void => {
        accessor.removeZone(zoneId);
      });
      this.viewZoneId = null;
    }

    this.decorations?.clear();
    this.decorations = null;

    this.contentSubscription?.dispose();
    this.contentSubscription = null;
  }

  dispose(): void {
    this.clear();
  }

  private addViewZone(plan: IPlannedCodeInsertion, previewLines: string[], languageId: string): void {
    const fontInfo: Monaco.editor.FontInfo = this.editor.getOption(this.monaco.editor.EditorOption.fontInfo);

    const domNode: HTMLDivElement = document.createElement("div");
    domNode.className = "kuika__ai-pending-edit__zone";
    domNode.style.fontFamily = fontInfo.fontFamily;
    domNode.style.fontSize = `${fontInfo.fontSize}px`;
    domNode.style.lineHeight = `${fontInfo.lineHeight}px`;
    // Shown unhighlighted until the colorizer answers, so the preview is never blank.
    domNode.textContent = previewLines.join("\n");

    const marginDomNode: HTMLDivElement = document.createElement("div");
    marginDomNode.className = "kuika__ai-pending-edit__margin";
    marginDomNode.style.fontSize = `${fontInfo.fontSize}px`;
    previewLines.forEach((): void => {
      // One block per line: Monaco's margin resets the container to display:block.
      const marker: HTMLDivElement = document.createElement("div");
      marker.style.height = `${fontInfo.lineHeight}px`;
      marker.style.lineHeight = `${fontInfo.lineHeight}px`;
      marker.textContent = "+";
      marginDomNode.appendChild(marker);
    });

    this.editor.changeViewZones((accessor: Monaco.editor.IViewZoneChangeAccessor): void => {
      this.viewZoneId = accessor.addZone({
        afterLineNumber: plan.previewAfterLineNumber,
        heightInLines: previewLines.length,
        domNode,
        marginDomNode
      });
    });

    this.colorizePreview(domNode, previewLines.join("\n"), languageId);
  }

  private colorizePreview(domNode: HTMLDivElement, text: string, languageId: string): void {
    const token: number = this.renderToken;
    this.monaco.editor
      .colorize(text, languageId, { tabSize: this.editor.getModel()?.getOptions().tabSize })
      .then((html: string): void => {
        if (token !== this.renderToken) return;
        domNode.innerHTML = html;
      })
      .catch((): void => {
        // The plain-text fallback is already on screen.
      });
  }

  private markReplacedLines(plan: IPlannedCodeInsertion): void {
    if (!plan.replacedRange) return;

    this.decorations = this.editor.createDecorationsCollection([
      {
        range: new this.monaco.Range(
          plan.replacedRange.startLineNumber,
          plan.replacedRange.startColumn,
          plan.replacedRange.endLineNumber,
          plan.replacedRange.endColumn
        ),
        options: {
          // inlineClassName, not className: only the inline variant reaches the text spans,
          // and the strike-through is the point.
          inlineClassName: "kuika__ai-pending-edit__removed",
          marginClassName: "kuika__ai-pending-edit__removed-margin",
          stickiness: this.monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges
        }
      }
    ]);
  }
}
